import React, { useMemo } from 'react'

import {
    Area,
    AreaConfig,
    AreaStatusConfig,
    AreaStatusTag,
    AreaTransferTooltipMap,
    BaseWorkorder,
    Damage,
    Folder,
    getFlatInfrastructure,
    IdBoolMap,
    ModelListMap,
    ModelMap,
    Schedule,
    ServiceContractMap,
    Unit,
    UnitConfig,
    WorkSpaceUser,
} from '../../../models'

import { FolderRow } from './FolderRow'
import { ManualUnitReadyMark, ManualUnitReadyStatus } from '../unitReady'

import { Container } from '../../../components'
import { SchedulerUnit } from './'

import { ScheduleFilterController } from '../../../hooks/useFilter'
import { usePagination } from '../../../hooks'
import { Pagination } from '@material-ui/lab'
import { Theme } from '@material-ui/core'
import {
    AreaStatusTagListThunk,
    BulkEditAreaStatusTagRequest,
} from '../../../store'
import { TimezoneState } from '../../../contexts/timezone/timezone'
import { SimpleUnitInspection } from '../../../hooks/useUnitInspections'
interface Props {
    prunedTree: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    workspaceUser: WorkSpaceUser | undefined
    schedule?: Schedule
    serviceAreaMap: IdBoolMap
    selectedWorkorder?: BaseWorkorder
    editMode: boolean
    editWorkorderSelection: ModelMap<BaseWorkorder>
    contentRef?: React.RefObject<HTMLDivElement>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    areaTransferTooltipMap: AreaTransferTooltipMap
    areaStatusConfigList: AreaStatusConfig[]
    selectedUnit?: Unit
    unitDamageMap: ModelListMap<Damage>
    upstreamContractMap: ServiceContractMap
    vacantAreas: Area[]
    filterController: ScheduleFilterController
    theme: Theme
    timezoneState: TimezoneState
    unitInspectionUnitMap: ModelMap<SimpleUnitInspection>
    manualUnitReady: Map<number, ManualUnitReadyMark>
    setUnitReadyStatus: (
        unitId: number,
        status: ManualUnitReadyStatus | null,
    ) => void
    onSelectWorkorder: (workorder: BaseWorkorder, unit: Unit) => void
    clickAssign: (unit: Unit) => void
    clickDamage: (unit: Unit) => void
    onClickChangeOrder: (workorder: BaseWorkorder) => void
    clickUnitNotes: (unit: Unit) => void
    bulkEditAreaStatusTag: (
        req: BulkEditAreaStatusTagRequest,
    ) => AreaStatusTagListThunk
    onClickUnitInspection: (simpleUnitInspection: SimpleUnitInspection) => void
}

export const SchedulerBody = (props: Props) => {
    const { prunedTree, contentRef } = props

    const flatInfrastructure = useMemo(() => {
        return getFlatInfrastructure(prunedTree)
    }, [prunedTree])

    const { pageData, numPages, page, setPage } = usePagination(
        flatInfrastructure,
        25,
    )

    const listHeight = 'calc(100vh - 240px)'

    return (
        <Container
            style={{
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden',
                overflowY: 'auto',
            }}
            scrollY
            ref={contentRef}
        >
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    height:
                        typeof listHeight === 'string'
                            ? parseInt(listHeight, 10) || 400
                            : listHeight,
                    overflowY: 'auto',
                }}
            >
                {pageData.map((item) => (
                    <Row
                        key={`SCHEDULE_ROW_${item.id}`}
                        item={item}
                        {...props}
                    />
                ))}
            </div>

            <Container
                style={{
                    height: 40,
                    borderTop: `1px solid ${props.theme.palette.divider}`,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {numPages > 1 && (
                    <Pagination
                        count={numPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                        size="small"
                        style={{ marginLeft: 16 }}
                    />
                )}
            </Container>
        </Container>
    )
}

interface RowProps extends Props {
    item: Folder | Unit
}

const _Row = (props: RowProps) => {
    const { item } = props

    const isUnit = (item as any).parent === undefined
    if (isUnit) {
        const unit = item as Unit
        return (
            <SchedulerUnit
                key={`SCHEDULE_UNIT_COMPONENT_${unit.id}`}
                {...props}
                unit={unit}
                unitConfig={props.unitConfigMap[unit.unit_config]}
                style={{}}
                selected={props.selectedUnit?.id === unit.id}
                damageCount={props.unitDamageMap[unit.id]?.length ?? 0}
                bulkEditAreaStatusTag={props.bulkEditAreaStatusTag}
                timezoneState={props.timezoneState}
            />
        )
    } else {
        const folder = item as Folder
        return <FolderRow style={{}} name={folder.path + folder.name} />
    }
}

const Row = React.memo(_Row)
